import { FileText, Film, Users, Lock, Layers, ExternalLink, RefreshCw, MessageSquarePlus, DoorOpen, Loader2, AlertTriangle, ChevronDown } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../../components/ui/dropdown-menu";
import { useMe } from "../../../lib/rehearsalRoomQueries";
import { InteractiveFileBadge } from "../shared/InteractiveFileBadge";

const CONTEXT_LABELS = {
  presentation: "Presentation", idea_card: "Idea", section: "Section", assignment: "Assignment",
  interaction_instance: "Practice", interaction_attempt: "Attempt", session: "Session", layer: "Layer",
};

function statusBadge(status) {
  if (status === "ready") return null;
  const map = {
    initiated: { label: "Preparing…", cls: "text-ink-soft" },
    uploading: { label: "Uploading…", cls: "text-ink-soft" },
    uploaded: { label: "Uploaded", cls: "text-ink-soft" },
    processing: { label: "Processing…", cls: "text-ink-soft" },
    error: { label: "Upload failed", cls: "text-red-600" },
    archived: { label: "Archived", cls: "text-ink-soft" },
  };
  const info = map[status] || { label: status, cls: "text-ink-soft" };
  return (
    <span className={`flex items-center gap-1 text-xs ${info.cls}`} data-testid="file-card-status">
      {status === "processing" ? <Loader2 size={11} className="animate-spin" /> : status === "error" ? <AlertTriangle size={11} /> : null}
      {info.label}
    </span>
  );
}

/**
 * Master v5 Prompt 11 — the Files card: uploader, audience, primary/
 * other contexts, version, Open context and a Request Feedback action.
 * "Request Feedback" is an HONEST placeholder — Feedback itself is a
 * later Prompt's scope, so this never pretends to send a real request.
 */
export default function FileCard({ entry, workspaceId, onOpen, onOpenWorkDocument, onReplace, onRemoveLink, onOpenRoom, onRequestFeedback }) {
  const { data: me } = useMe();
  const { asset, links, can_manage: canManage, task_linked: taskLinked, linked_work_documents: linkedDocs = [] } = entry;
  const primaryLink = links.find((l) => l.is_primary) || links[0];
  const otherLinks = links.filter((l) => l !== primaryLink);
  const isVideo = asset.kind === "video";
  const uploaderLabel = me && me.per_id === asset.uploaded_by ? "You" : "A team member";
  const audienceLabel = asset.visibility_scope === "workspace_shared" ? "Shared" : asset.visibility_scope === "coach_private" ? "Coach only" : "Private";

  return (
    <div className="border border-ink/15 bg-white/50 rounded-lg p-4 space-y-3" data-testid={`file-card-${asset.fas_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {isVideo ? <Film size={18} className="text-terracotta shrink-0" /> : <FileText size={18} className="text-ink-soft shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink truncate flex items-center gap-2" data-testid="file-card-filename">
              {asset.original_filename}
              {linkedDocs.length > 0 && <InteractiveFileBadge testId={`file-card-interactive-badge-${asset.fas_id}`} />}
            </p>
            <p className="text-xs text-ink-soft">v{asset.latest_version_number} · Uploaded by {uploaderLabel}</p>
          </div>
        </div>
        {statusBadge(asset.status)}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
        <span className="flex items-center gap-1" data-testid="file-card-audience">
          {asset.visibility_scope === "workspace_shared" ? <Users size={11} /> : <Lock size={11} />} {audienceLabel}
        </span>
        {primaryLink && (
          <span className="flex items-center gap-1 bg-terracotta/10 text-terracotta px-1.5 py-0.5 rounded" data-testid="file-card-primary-context">
            <Layers size={11} /> {CONTEXT_LABELS[primaryLink.target_type] || primaryLink.target_type}
          </span>
        )}
        {otherLinks.map((l) => (
          <span key={l.fln_id} className="flex items-center gap-1 bg-ink/5 px-1.5 py-0.5 rounded" data-testid={`file-card-other-context-${l.fln_id}`}>
            {CONTEXT_LABELS[l.target_type] || l.target_type}
            {canManage && (
              <button onClick={() => onRemoveLink(asset.fas_id, l.fln_id)} className="ml-0.5 hover:text-terracotta" aria-label="Remove context" data-testid={`file-card-remove-link-${l.fln_id}`}>×</button>
            )}
          </span>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-1">
        {linkedDocs.length > 1 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" data-testid={`file-card-open-${asset.fas_id}`}>
                <ExternalLink size={13} className="mr-1.5" /> Open <ChevronDown size={12} className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {linkedDocs.map((d) => (
                <DropdownMenuItem key={d.wdoc_id} onClick={() => onOpenWorkDocument(d.wdoc_id)} data-testid={`file-card-open-workdoc-${d.wdoc_id}`}>
                  {d.owner_member_name || d.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            size="sm" variant="outline" disabled={asset.status !== "ready"}
            onClick={() => (linkedDocs.length === 1 ? onOpenWorkDocument(linkedDocs[0].wdoc_id) : onOpen(entry))}
            data-testid={`file-card-open-${asset.fas_id}`}
          >
            <ExternalLink size={13} className="mr-1.5" /> Open
          </Button>
        )}
        {taskLinked && asset.status === "ready" && (
          <Button size="sm" variant="outline" onClick={() => onOpenRoom(asset.fas_id)} data-testid={`file-card-open-room-${asset.fas_id}`}>
            <DoorOpen size={13} className="mr-1.5" /> Open in Room
          </Button>
        )}
        {canManage && (
          <Button size="sm" variant="ghost" onClick={() => onReplace(entry)} data-testid={`file-card-replace-${asset.fas_id}`}>
            <RefreshCw size={13} className="mr-1.5" /> Replace
          </Button>
        )}
        <Button size="sm" variant="ghost" className="text-ink-soft" onClick={() => onRequestFeedback(asset.fas_id)} data-testid={`file-card-request-feedback-${asset.fas_id}`}>
          <MessageSquarePlus size={13} className="mr-1.5" /> Request Feedback
        </Button>
      </div>
    </div>
  );
}
